/**
 * T-085 — Page 3: access control (spec §5).
 *
 * Self-contained page for download-link lifecycle management:
 *  - Issue managed (revocable, proxied via /api/download/<token>) or direct
 *    GCS V4 (non-revocable bearer capability) links against the spec §3
 *    object-path allowlist — POST /api/links.
 *  - Inspect/revoke links server-side — GET /api/links, POST /api/links/:id/revoke
 *    (gcs_v4 links surface the 409 `non_revocable` envelope honestly).
 *  - Browse the allowlisted GCS object inventory — GET /api/objects.
 *  - Review privacy-minimized application audit events — GET /api/audit.
 *
 * Raw token values are never listable: a download URL is shown exactly once
 * at issuance and survives only in the session store (spec §5 Page 3).
 *
 * This file also exports the small shared controls (CopyButton, ErrorPanel,
 * TokenSignIn, IssueLinkForm) reused by BuildRunsPage — Main owns central
 * integration, so no shared components/ directory is created here.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  AUDIT_EVENT_TYPES,
  LINK_EXPIRIES,
  OBJECT_PREFIXES,
  isAllowlistedObjectPath,
} from '../../shared/api';
import type {
  AuditEvent,
  AuditEventType,
  CreateLinkResponse,
  GcsObjectSummary,
  LinkExpiry,
  LinkMode,
  LinkStatus,
  LinkSummary,
  ObjectPrefix,
} from '../../shared/api';
import { LINK_STATUSES } from '../../shared/api';
import { ApiError } from '../api';
import {
  sessionApi,
  sessionStore,
  useIssuedLinks,
  useSessionSnapshot,
  useSessionToken,
} from '../state';
import type { IssuedLink } from '../state';

const PAGE_LIMIT = 25;

/* ------------------------------------------------------------------------ */
/* Formatting / error helpers (module-local)                                 */
/* ------------------------------------------------------------------------ */

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

function formatBytes(size: number | null): string {
  if (size === null) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KiB', 'MiB', 'GiB'];
  let value = size;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value.toFixed(1)} ${unit}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `[${error.code}] ${error.message}`;
  if (error instanceof Error) return error.message;
  return String(error);
}

/* ------------------------------------------------------------------------ */
/* Shared small controls (reused by BuildRunsPage)                           */
/* ------------------------------------------------------------------------ */

/** Inline error banner rendering the shared error envelope code + message. */
export function ErrorPanel(props: { error: unknown; onDismiss?: () => void }) {
  const { error, onDismiss } = props;
  if (error === null || error === undefined) return null;
  return (
    <div className="sg-error-panel" role="alert">
      <span className="sg-error-text">{errorMessage(error)}</span>
      {onDismiss !== undefined && (
        <button type="button" className="sg-button sg-button--small" onClick={onDismiss}>
          Dismiss
        </button>
      )}
    </div>
  );
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path (permissions policy, non-secure context).
  }
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Client-only clipboard copy with transient feedback (spec §5 Page 2 note). */
export function CopyButton(props: { value: string; label?: string }) {
  const { value, label = 'Copy' } = props;
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = window.setTimeout(() => setState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className={`sg-button sg-button--small sg-copy--${state}`}
      onClick={() => {
        void copyText(value).then((ok) => setState(ok ? 'copied' : 'failed'));
      }}
    >
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label}
    </button>
  );
}

/**
 * Operator bearer-token entry. Rendered by pages whenever the session has no
 * token (signed out, or token dropped after a 401). Session-only persistence
 * via sessionStore (spec §3).
 */
export function TokenSignIn() {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent): void {
    event.preventDefault();
    try {
      sessionStore.setToken(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="sg-card sg-signin">
      <h2>Operator sign-in required</h2>
      <p className="sg-muted">
        Enter the operator bearer token (<code>SUPPORT_APP_TOKEN</code>). It is kept in
        session storage only and never leaves this browser except as an Authorization header
        to this API.
      </p>
      <form className="sg-signin-form" onSubmit={submit}>
        <input
          className="sg-input"
          type="password"
          aria-label="Operator token"
          placeholder="Operator token"
          autoComplete="off"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button className="sg-button sg-button--primary" type="submit" disabled={draft.trim() === ''}>
          Sign in
        </button>
      </form>
      {error !== null && <p className="sg-error-text">{error}</p>}
    </div>
  );
}

export interface IssueLinkFormProps {
  /** Allowlisted GCS object path to link (spec §3). */
  objectPath: string;
  /** Optional artifact_cache reconciliation hint. */
  artifactId?: string;
  /** When provided, the object path is editable (access-page draft box). */
  onObjectPathChange?: (path: string) => void;
  /** Called after a link was issued and recorded in the session store. */
  onIssued?: (response: CreateLinkResponse) => void;
}

/**
 * Link issuance form shared by the access page and the run artifact browser.
 * Managed links are the default (honestly revocable, spec §4); `gcs_v4`
 * direct URLs are labeled non-revocable. The one-time URL is displayed inline
 * and recorded in the session store.
 */
export function IssueLinkForm(props: IssueLinkFormProps) {
  const { objectPath, artifactId, onObjectPathChange, onIssued } = props;
  const expiry = useSessionSnapshot().expiry;
  const [mode, setMode] = useState<LinkMode>('managed');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [issued, setIssued] = useState<CreateLinkResponse | null>(null);

  const trimmedPath = objectPath.trim();
  const pathValid = isAllowlistedObjectPath(trimmedPath);
  const targetKey = `${trimmedPath}|${artifactId ?? ''}`;

  // A changed target invalidates any previously issued one-time URL panel.
  useEffect(() => {
    setIssued(null);
    setError(null);
  }, [targetKey]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!pathValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await sessionApi.createLink({
        objectPath: trimmedPath,
        ...(artifactId !== undefined ? { artifactId } : {}),
        mode,
        expiry,
      });
      sessionStore.recordIssuedLink(response);
      setIssued(response);
      onIssued?.(response);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="sg-issue-form" onSubmit={(event) => void submit(event)}>
      <div className="sg-field">
        <label className="sg-label" htmlFor="issue-object-path">
          Object path
        </label>
        {onObjectPathChange !== undefined ? (
          <input
            id="issue-object-path"
            className="sg-input sg-input--mono"
            type="text"
            spellCheck={false}
            placeholder="debs/run-123/py3.10/tnd-core_1.4.2_amd64.deb"
            value={objectPath}
            onChange={(event) => onObjectPathChange(event.target.value)}
          />
        ) : (
          <code className="sg-path">{trimmedPath}</code>
        )}
        {trimmedPath !== '' && !pathValid && (
          <p className="sg-warning">
            Path is outside the allowlist: <code>debs/run-&lt;id&gt;/py3.10|py3.12/….deb</code> or{' '}
            <code>releases/inference-engine/v/&lt;semver&gt;/py3.10|py3.12/…</code>.
          </p>
        )}
      </div>

      <fieldset className="sg-field sg-mode-field">
        <legend className="sg-label">Link mode</legend>
        <label className="sg-radio">
          <input
            type="radio"
            name="link-mode"
            checked={mode === 'managed'}
            onChange={() => setMode('managed')}
          />
          <span>
            <strong>Managed</strong> — proxied via <code>/api/download/&lt;token&gt;</code>, revocable
            (default)
          </span>
        </label>
        <label className="sg-radio">
          <input
            type="radio"
            name="link-mode"
            checked={mode === 'gcs_v4'}
            onChange={() => setMode('gcs_v4')}
          />
          <span>
            <strong>Direct GCS V4 URL</strong> — <span className="sg-warning">non-revocable</span>{' '}
            after issuance (bearer capability, spec §4)
          </span>
        </label>
      </fieldset>

      <div className="sg-field sg-expiry-field">
        <label className="sg-label" htmlFor="issue-expiry">
          Expiry
        </label>
        <select
          id="issue-expiry"
          className="sg-select"
          value={expiry}
          onChange={(event) => sessionStore.setExpiry(event.target.value as LinkExpiry)}
        >
          {LINK_EXPIRIES.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      </div>

      <div className="sg-actions">
        <button
          className="sg-button sg-button--primary"
          type="submit"
          disabled={!pathValid || submitting}
        >
          {submitting ? 'Issuing…' : 'Issue link'}
        </button>
      </div>

      <ErrorPanel error={error} onDismiss={() => setError(null)} />

      {issued !== null && (
        <div className="sg-issued-panel">
          <p className="sg-issued-title">
            Link issued — this URL is shown <strong>once</strong> and is never listable again:
          </p>
          <div className="sg-issued-url">
            <code>{sessionApi.resolveUrl(issued.url)}</code>
            <CopyButton value={sessionApi.resolveUrl(issued.url)} label="Copy URL" />
          </div>
          {!issued.revocable && (
            <p className="sg-warning">
              Direct GCS V4 URL: deleting its record does not invalidate it (spec §4).
            </p>
          )}
        </div>
      )}
    </form>
  );
}

/* ------------------------------------------------------------------------ */
/* Badges                                                                    */
/* ------------------------------------------------------------------------ */

function LinkStatusBadge(props: { status: LinkStatus }) {
  const { status } = props;
  return <span className={`sg-badge sg-badge--${status}`}>{status}</span>;
}

function LinkModeBadge(props: { mode: LinkMode }) {
  const { mode } = props;
  return (
    <span className={`sg-badge ${mode === 'managed' ? 'sg-badge--managed' : 'sg-badge--direct'}`}>
      {mode === 'managed' ? 'managed' : 'gcs_v4'}
    </span>
  );
}

/* ------------------------------------------------------------------------ */
/* Session-issued links (one-time URLs survive only here)                    */
/* ------------------------------------------------------------------------ */

function SessionIssuedLinks() {
  const issuedLinks = useIssuedLinks();
  const [error, setError] = useState<unknown | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  async function revoke(entry: IssuedLink): Promise<void> {
    setRevokingId(entry.link.id);
    setError(null);
    try {
      const response = await sessionApi.revokeLink(entry.link.id);
      sessionStore.applyLinkUpdate(response.link);
    } catch (err) {
      setError(err);
    } finally {
      setRevokingId(null);
    }
  }

  if (issuedLinks.length === 0) return null;

  return (
    <section className="sg-card">
      <h2>Issued this session</h2>
      <p className="sg-muted">
        One-time download URLs for links issued from this browser session. They are not recoverable
        after sign-out.
      </p>
      <ErrorPanel error={error} onDismiss={() => setError(null)} />
      <div className="sg-table-wrap">
        <table className="sg-table">
          <thead>
            <tr>
              <th>Issued</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Object path</th>
              <th>Download URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {issuedLinks.map((entry) => {
              const absoluteUrl = sessionApi.resolveUrl(entry.url);
              return (
                <tr key={entry.link.id}>
                  <td>{formatDateTime(entry.issuedAt)}</td>
                  <td>
                    <LinkModeBadge mode={entry.link.mode} />
                  </td>
                  <td>
                    <LinkStatusBadge status={entry.link.status} />
                  </td>
                  <td>
                    <code className="sg-path">{entry.link.objectPath}</code>
                  </td>
                  <td className="sg-url-cell">
                    <code className="sg-path">{absoluteUrl}</code>
                    <CopyButton value={absoluteUrl} label="Copy URL" />
                  </td>
                  <td>
                    {entry.revocable && entry.link.status === 'active' ? (
                      <button
                        type="button"
                        className="sg-button sg-button--small sg-button--danger"
                        disabled={revokingId === entry.link.id}
                        onClick={() => void revoke(entry)}
                      >
                        {revokingId === entry.link.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    ) : (
                      <span className="sg-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Server-side link list (GET /api/links)                                    */
/* ------------------------------------------------------------------------ */

function LinksBrowser() {
  const [statusFilter, setStatusFilter] = useState<'' | LinkStatus>('');
  const [links, setLinks] = useState<LinkSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    sessionApi
      .listLinks(
        { ...(statusFilter !== '' ? { status: statusFilter } : {}), limit: PAGE_LIMIT },
        controller.signal,
      )
      .then((response) => {
        setLinks(response.links);
        setNextCursor(response.nextCursor);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [statusFilter, reloadKey]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await sessionApi.listLinks({
        ...(statusFilter !== '' ? { status: statusFilter } : {}),
        cursor: nextCursor,
        limit: PAGE_LIMIT,
      });
      setLinks((previous) => [...previous, ...response.links]);
      setNextCursor(response.nextCursor);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function revoke(link: LinkSummary): Promise<void> {
    setRevokingId(link.id);
    setError(null);
    try {
      const response = await sessionApi.revokeLink(link.id);
      setLinks((previous) => previous.map((row) => (row.id === link.id ? response.link : row)));
      sessionStore.applyLinkUpdate(response.link);
    } catch (err) {
      // 409 `non_revocable` (gcs_v4) lands here and is surfaced honestly.
      setError(err);
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <section className="sg-card">
      <div className="sg-card-header">
        <h2>Download links</h2>
        <div className="sg-toolbar">
          <label className="sg-toolbar-field">
            Status
            <select
              className="sg-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as '' | LinkStatus)}
            >
              <option value="">all</option>
              {LINK_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="sg-button sg-button--small"
            disabled={loading}
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Refresh
          </button>
        </div>
      </div>
      <p className="sg-muted">
        Token values are never returned after creation; only issuance-time URLs (session list above)
        are copyable. Revoking a managed link makes subsequent downloads return <code>410</code>.
      </p>
      <ErrorPanel error={error} onDismiss={() => setError(null)} />
      {loading ? (
        <p className="sg-muted">Loading links…</p>
      ) : links.length === 0 ? (
        <p className="sg-muted">No links match this filter.</p>
      ) : (
        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Object path</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>Downloads</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id}>
                  <td title={link.id}>
                    <code>{link.id.length <= 10 ? link.id : `${link.id.slice(0, 8)}…`}</code>
                  </td>
                  <td>
                    <LinkModeBadge mode={link.mode} />
                  </td>
                  <td>
                    <LinkStatusBadge status={link.status} />
                  </td>
                  <td>
                    <code className="sg-path">{link.objectPath}</code>
                  </td>
                  <td>{formatDateTime(link.createdAt)}</td>
                  <td>{formatDateTime(link.expiresAt)}</td>
                  <td>{formatDateTime(link.lastUsedAt)}</td>
                  <td className="sg-numeric">{link.downloadCount}</td>
                  <td>
                    {link.mode === 'managed' && link.status === 'active' ? (
                      <button
                        type="button"
                        className="sg-button sg-button--small sg-button--danger"
                        disabled={revokingId === link.id}
                        onClick={() => void revoke(link)}
                      >
                        {revokingId === link.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    ) : link.mode === 'gcs_v4' ? (
                      <span className="sg-muted" title="Direct GCS V4 URLs cannot be revoked (spec §4)">
                        non-revocable
                      </span>
                    ) : (
                      <span className="sg-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor !== null && (
        <div className="sg-actions">
          <button
            type="button"
            className="sg-button"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Allowlisted object browser (GET /api/objects)                             */
/* ------------------------------------------------------------------------ */

function ObjectsBrowser(props: { onUseObject: (objectPath: string) => void }) {
  const { onUseObject } = props;
  const [prefix, setPrefix] = useState<ObjectPrefix>('debs');
  const [objects, setObjects] = useState<GcsObjectSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    sessionApi
      .listObjects({ prefix, limit: PAGE_LIMIT }, controller.signal)
      .then((response) => {
        setObjects(response.objects);
        setNextCursor(response.nextCursor);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [prefix, reloadKey]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await sessionApi.listObjects({ prefix, cursor: nextCursor, limit: PAGE_LIMIT });
      setObjects((previous) => [...previous, ...response.objects]);
      setNextCursor(response.nextCursor);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="sg-card">
      <div className="sg-card-header">
        <h2>Object browser</h2>
        <div className="sg-toolbar">
          <label className="sg-toolbar-field">
            Prefix
            <select
              className="sg-select"
              value={prefix}
              onChange={(event) => setPrefix(event.target.value as ObjectPrefix)}
            >
              {OBJECT_PREFIXES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="sg-button sg-button--small"
            disabled={loading}
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Refresh
          </button>
        </div>
      </div>
      <p className="sg-muted">
        Allowlisted GCS inventory (<code>debs/</code>, <code>releases/</code>). “Use” prefills the
        issuance form above.
      </p>
      <ErrorPanel error={error} onDismiss={() => setError(null)} />
      {loading ? (
        <p className="sg-muted">Loading objects…</p>
      ) : objects.length === 0 ? (
        <p className="sg-muted">No objects under this prefix.</p>
      ) : (
        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th>Object path</th>
                <th>Size</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {objects.map((object) => (
                <tr key={object.objectPath}>
                  <td>
                    <code className="sg-path">{object.objectPath}</code>
                  </td>
                  <td>{formatBytes(object.sizeBytes)}</td>
                  <td>{formatDateTime(object.updatedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="sg-button sg-button--small"
                      onClick={() => onUseObject(object.objectPath)}
                    >
                      Use
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor !== null && (
        <div className="sg-actions">
          <button
            type="button"
            className="sg-button"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Application audit log (GET /api/audit)                                    */
/* ------------------------------------------------------------------------ */

function AuditLog() {
  const [eventType, setEventType] = useState<'' | AuditEventType>('');
  const [linkIdFilter, setLinkIdFilter] = useState('');
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const trimmedLinkId = linkIdFilter.trim();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    sessionApi
      .listAudit(
        {
          ...(eventType !== '' ? { eventType } : {}),
          ...(trimmedLinkId !== '' ? { linkId: trimmedLinkId } : {}),
          limit: PAGE_LIMIT,
        },
        controller.signal,
      )
      .then((response) => {
        setEvents(response.events);
        setNextCursor(response.nextCursor);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [eventType, trimmedLinkId, reloadKey]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await sessionApi.listAudit({
        ...(eventType !== '' ? { eventType } : {}),
        ...(trimmedLinkId !== '' ? { linkId: trimmedLinkId } : {}),
        cursor: nextCursor,
        limit: PAGE_LIMIT,
      });
      setEvents((previous) => [...previous, ...response.events]);
      setNextCursor(response.nextCursor);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="sg-card">
      <div className="sg-card-header">
        <h2>Audit log</h2>
        <div className="sg-toolbar">
          <label className="sg-toolbar-field">
            Event
            <select
              className="sg-select"
              value={eventType}
              onChange={(event) => setEventType(event.target.value as '' | AuditEventType)}
            >
              <option value="">all</option>
              {AUDIT_EVENT_TYPES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
          <label className="sg-toolbar-field">
            Link ID
            <input
              className="sg-input sg-input--mono"
              type="text"
              spellCheck={false}
              placeholder="filter by link id"
              value={linkIdFilter}
              onChange={(event) => setLinkIdFilter(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="sg-button sg-button--small"
            disabled={loading}
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Refresh
          </button>
        </div>
      </div>
      <p className="sg-muted">
        Application audit events only (issuance, download, revocation, provider errors). GCS Cloud
        Audit Logs integration is a follow-up and is not assumed available (spec §5).
      </p>
      <ErrorPanel error={error} onDismiss={() => setError(null)} />
      {loading ? (
        <p className="sg-muted">Loading audit events…</p>
      ) : events.length === 0 ? (
        <p className="sg-muted">No audit events match this filter.</p>
      ) : (
        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Link</th>
                <th>Object path</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const detail = event.detail === null ? '' : JSON.stringify(event.detail);
                return (
                  <tr key={event.id}>
                    <td>{formatDateTime(event.createdAt)}</td>
                    <td>
                      <span className={`sg-badge sg-badge--event-${event.eventType}`}>
                        {event.eventType}
                      </span>
                    </td>
                    <td>
                      {event.linkId !== null ? (
                        <code title={event.linkId}>
                          {event.linkId.length <= 10 ? event.linkId : `${event.linkId.slice(0, 8)}…`}
                        </code>
                      ) : (
                        <span className="sg-muted">—</span>
                      )}
                    </td>
                    <td>
                      {event.objectPath !== null ? (
                        <code className="sg-path">{event.objectPath}</code>
                      ) : (
                        <span className="sg-muted">—</span>
                      )}
                    </td>
                    <td className="sg-detail-cell" title={detail}>
                      {detail === '' ? <span className="sg-muted">—</span> : detail}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor !== null && (
        <div className="sg-actions">
          <button
            type="button"
            className="sg-button"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

export default function AccessControlPage() {
  const token = useSessionToken();
  const [draftPath, setDraftPath] = useState('');

  if (token === null) {
    return (
      <section className="sg-page access-page">
        <h1>Access Control</h1>
        <TokenSignIn />
      </section>
    );
  }

  return (
    <section className="sg-page access-page">
      <h1>Access Control</h1>
      <p className="sg-muted">
        Issue expiring download links against the allowlisted artifact inventory, revoke managed
        links, and review application audit events. Managed links are the default because they are
        honestly revocable; direct GCS V4 URLs are bearer capabilities and cannot be revoked after
        issuance (spec §4).
      </p>

      <section className="sg-card" id="issue-link-card">
        <h2>Issue download link</h2>
        <IssueLinkForm objectPath={draftPath} onObjectPathChange={setDraftPath} />
      </section>

      <SessionIssuedLinks />
      <LinksBrowser />
      <ObjectsBrowser
        onUseObject={(path) => {
          setDraftPath(path);
          document.getElementById('issue-link-card')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }}
      />
      <AuditLog />
    </section>
  );
}
